import React, { useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { GlobalContext } from "../../context/globalContext";
import RequestEventAdminAccess from "../../components/RequestEventAdminAccess/RequestEventAdminAccess";
import RequestPostEventsAccess from "../../components/RequestPostEventsAccess/RequestPostEventsAccess";
import {
  AdminAccessRequest,
  getMyAccessRequests,
  getMyAdminEvents,
  isSuperAdminRole,
  MyAdminEvent,
} from "../../api/admin";
import LoadingPanel from "../../components/LoadingPanel/LoadingPanel";
import ErrorAlert from "../../components/ErrorAlert/ErrorAlert";
import { getUserFacingErrorMessage } from "../../utils/userFacingError";
import ManagedEventsTable from "./ManagedEventsTable";

export default function UserPageManageEvents() {
  const { currentUser } = useContext(GlobalContext);
  const [events, setEvents] = useState<MyAdminEvent[]>([]);
  const [canPostEvents, setCanPostEvents] = useState(false);
  const [requests, setRequests] = useState<AdminAccessRequest[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const isSuperAdmin = isSuperAdminRole(currentUser?.role);
  const activeEvents = events.filter((e) => e.isSuperAdmin || e.grantedAt);
  const eventAdminRequests = requests.filter(
    (r) => r.requestType !== "post_events",
  );
  const postEventsRequests = requests.filter(
    (r) => r.requestType === "post_events",
  );
  const isApprovedAdmin =
    isSuperAdmin || activeEvents.length > 0 || canPostEvents;
  const canCreateNewEvents = isSuperAdmin || canPostEvents;
  const hasFullAccessOnAssigned = activeEvents.some(
    (e) => e.isSuperAdmin || e.permissionLevel === "full",
  );

  const load = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const adminData = await getMyAdminEvents();
      const myRequests = isSuperAdmin ? [] : await getMyAccessRequests();
      setEvents(adminData.events);
      setCanPostEvents(adminData.canPostEvents);
      setRequests(myRequests);
    } catch (err) {
      setError(getUserFacingErrorMessage(err, "Failed to load events."));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (currentUser) load();
  }, [currentUser]);

  if (!currentUser) {
    return <p>Please sign in.</p>;
  }

  if (isLoading) {
    return (
      <LoadingPanel
        title="Loading manage events"
        subtitle="Retrieving your events and access."
        className="py-12"
      />
    );
  }

  if (!isApprovedAdmin) {
    return (
      <div>
        <h2 className="font-bold text-lg text-slate-900 mb-1">Manage events</h2>
        <p className="text-sm text-slate-600 mb-8">
          Request access to administer events or to create new events. A super
          admin will review your request.
        </p>
        {error && <ErrorAlert className="mb-4">{error}</ErrorAlert>}

        <section className="mb-10">
          <h3 className="font-semibold text-base mb-2">Event admin access</h3>
          <p className="text-sm text-slate-600 mb-4">
            Get access to manage an existing event (registrations or full
            management, as approved).
          </p>
          <RequestEventAdminAccess
            requests={eventAdminRequests}
            onRequestSubmitted={load}
          />
        </section>

        <section>
          <h3 className="font-semibold text-base mb-2">Post events permission</h3>
          <p className="text-sm text-slate-600 mb-4">
            Create new events on EventsHouse. Events you create will grant you
            full management access automatically.
          </p>
          <RequestPostEventsAccess
            requests={postEventsRequests}
            onRequestSubmitted={load}
            granted={canPostEvents}
          />
        </section>
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-col sm:flex-row justify-between items-start gap-4 mb-6">
        <div>
          <h2 className="font-bold text-lg text-slate-900">Manage events</h2>
          <p className="text-sm text-slate-600 mt-1">
            {hasFullAccessOnAssigned || canCreateNewEvents
              ? "Edit and administer your events."
              : "View and administer events you have been assigned."}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {isSuperAdmin && (
            <Link to="/user/events/new" className="eh-btn-primary text-sm">
              Create event
            </Link>
          )}
          {!isSuperAdmin && (
            <RequestEventAdminAccess
              requests={[]}
              onRequestSubmitted={load}
              compact
            />
          )}
        </div>
      </div>

      {error && <ErrorAlert className="mb-4">{error}</ErrorAlert>}

      <ManagedEventsTable
        events={activeEvents}
        requests={requests}
        onRefresh={load}
        alwaysShowEdit={isSuperAdmin}
        showAccessColumn={!isSuperAdmin}
        emptyMessage={
          isSuperAdmin ? "No events yet." : undefined
        }
      />

      {!isSuperAdmin && (
        <section className="mt-10 pt-8 border-t border-slate-200">
          <h3 className="font-semibold text-lg mb-2">Post events</h3>
          <p className="text-sm text-slate-600 mb-4">
            {canCreateNewEvents
              ? "You can create new events on EventsHouse. Each event you create grants you full management access automatically."
              : "Request permission to create new events on EventsHouse."}
          </p>
          {canCreateNewEvents ? (
            <Link to="/user/events/new" className="eh-btn-primary text-sm">
              Create event
            </Link>
          ) : (
            <RequestPostEventsAccess
              requests={postEventsRequests}
              onRequestSubmitted={load}
              granted={canPostEvents}
            />
          )}
        </section>
      )}

      {!isSuperAdmin && (
        <section className="mt-10 pt-8 border-t border-slate-200">
          <h3 className="font-semibold text-lg mb-4">Access requests</h3>
          {requests.length === 0 ? (
            <p className="text-slate-600 text-sm">No requests yet.</p>
          ) : (
            <div className="space-y-3">
              {requests.map((req) => (
                <div
                  key={req.id}
                  className="border p-4 rounded-sm bg-white text-sm"
                >
                  <p className="font-medium">
                    {req.requestType === "post_events"
                      ? "Post events permission"
                      : (req.event?.title ?? "Event admin access")}
                  </p>
                  <p className="text-slate-600 mt-1">
                    Status:{" "}
                    <span
                      className={
                        req.status === "approved"
                          ? "text-green-700 font-medium"
                          : req.status === "rejected"
                            ? "text-red-700 font-medium"
                            : "text-amber-700 font-medium"
                      }
                    >
                      {req.status}
                    </span>
                    {" · "}
                    Submitted {req.createdAt.slice(0, 10)}
                  </p>
                  {req.message && (
                    <p className="mt-2 text-slate-700">{req.message}</p>
                  )}
                  {req.reviewNote && (
                    <p className="mt-1 text-slate-500">Note: {req.reviewNote}</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>
      )}
    </div>
  );
}
